import { toUpper } from 'lodash';
import pluralize from 'pluralize';
import { type MouseEvent, memo, useCallback, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type BaseCustomField, type Clip } from '@air/api/types';
import { EllipsisIcon, LayersIcon, MessageSquareIcon } from '@air/design-icons';
import { formatBytes } from '@air/utilities';
import { isPdfRenderedExtension } from '@air/utils-clips';

import { CardAction } from '~/components/CardAction';
import { CustomFieldsDisplay } from '~/components/Gallery/CustomFieldsDisplay';
import { GalleryCardCustomFieldsAction } from '~/components/Gallery/GalleryCardCustomFieldsAction';
import { GalleryItemType } from '~/components/Gallery/types';
import { getAssetCustomFields } from '~/components/Gallery/utils';
import { GalleryFileMenu } from '~/components/v2/components/shared/gallery/GalleryFileMenu';
import { GalleryFilePreviewIcon } from '~/components/v2/components/shared/gallery/GalleryFilePreviewIcon';
import { GalleryFileTitle } from '~/components/v2/components/shared/gallery/GalleryFileTitle';
import {
  GallerySelectableCard,
  type GallerySelectableCardProps,
} from '~/components/v2/components/shared/gallery/GallerySelectableCard';
import { visibleCustomFieldsViewOptionsSelector } from '~/store/configViews/selectors';
import { type SelectableGalleryFileItem } from '~/store/selectedItems/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export type GalleryFileProps<C> = Pick<
  GallerySelectableCardProps<SelectableGalleryFileItem>,
  'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable'
> & {
  allCustomFields: BaseCustomField[] | undefined;
  canViewCustomFields: boolean;
  file: C;
  onCustomFieldsClick: () => void;
  onDiscussionsClick: () => void;
  onFileClick: () => void;
  onFileTitleCmdClick?: () => void;
  onVersionsClick: () => void;
  shouldShowDiscussions: boolean;
  showVersionsIndicator: boolean;
};

const _GalleryFile = <C extends Clip | PublicClip>({
  allCustomFields,
  canViewCustomFields,
  file,
  getItemMenuOptions,
  getSelectionMenuOptions,
  isSelectable,
  onCustomFieldsClick,
  onDiscussionsClick,
  onFileClick,
  onFileTitleCmdClick,
  onVersionsClick,
  shouldShowDiscussions,
  showVersionsIndicator,
}: GalleryFileProps<C>) => {
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);

  const item = useMemo<SelectableGalleryFileItem>(
    () => ({
      id: file.id,
      item: file,
      type: GalleryItemType.file,
    }),
    [file],
  );

  const customFields = useMemo(
    () => getAssetCustomFields({ allCustomFields: allCustomFields || [], clip: file, visibleCustomFields }),
    [allCustomFields, file, visibleCustomFields],
  );

  const popoverCustomFields = useMemo(() => {
    // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
    return file?.customFields?.filter((customField) => {
      return allCustomFields?.some((cf) => cf.id === customField.id);
    });
    // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
  }, [allCustomFields, file?.customFields]);

  const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;

  const meta = useMemo(() => {
    const meta = [];

    if (file.ext) {
      meta.push(toUpper(file.ext));
    }

    if (file.size) {
      meta.push(formatBytes(file.size));
    }

    if (file.pages) {
      meta.push(pluralize('page', file.pages, true));
    }

    if (!isPdfRenderedExtension(file.ext) && file.width && file.height) {
      meta.push(`${file.width} x ${file.height}`);
    }

    return meta.join(' · ');
  }, [file.ext, file.height, file.pages, file.size, file.width]);

  const onTitleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if ((event.metaKey || event.ctrlKey) && onFileTitleCmdClick) {
        onFileTitleCmdClick?.();
      } else {
        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onFileClick?.();
      }
    },
    [onFileClick, onFileTitleCmdClick],
  );

  return (
    <GallerySelectableCard
      data-testid="FILE_CARD"
      data-file-id={file.id}
      className="flex size-full flex-col rounded-16 bg-transparent ring-2 ring-transparent ring-inset transition-colors ads:hover:bg-hover-subtle hover:bg-gray-4 ads:data-[highlighted=true]:ring-focus ads:data-[selected=true]:ring-primary data-[highlighted=true]:ring-blue-9 data-[selected=true]:ring-blue-9"
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      isSelectable={isSelectable}
      item={item}
      onClick={onFileClick}
    >
      {({ isHovering, setIsMenuOpen }) => (
        <>
          <div className="pointer-events-none relative grow">
            <div className="absolute inset-1 flex flex-col justify-between overflow-hidden rounded-12 ads:bg-raised bg-gray-2">
              <div className="flex justify-between p-3">
                <div className="flex items-center gap-1">
                  <GalleryFilePreviewIcon file={file} />

                  {/*
                   * These icons are intentionally aligned center to the GalleryFilePreviewIcon and will
                   * be misaligned with the GalleryFileMenu.
                   * @see https://air-labs-team.slack.com/archives/C08TRCPS3GC/p1754077383902129?thread_ts=1754077134.071879&cid=C08TRCPS3GC
                   */}
                  <div className="flex items-center gap-1">
                    {canViewCustomFields && !!popoverCustomFields?.length && (
                      <GalleryCardCustomFieldsAction customFields={popoverCustomFields} onClick={onCustomFieldsClick} />
                    )}
                    {showVersionsIndicator && !!onVersionsClick && (
                      <CardAction
                        data-testid="CARD_VERSIONS_INDICATOR"
                        icon={<LayersIcon size="sm" />}
                        onClick={onVersionsClick}
                      />
                    )}
                    {shouldShowDiscussions && !!onDiscussionsClick && !!file.openCommentCount && (
                      <CardAction
                        data-testid="CARD_DISCUSSIONS_INDICATOR"
                        onClick={onDiscussionsClick}
                        icon={<MessageSquareIcon size="sm" />}
                        text={file.openCommentCount}
                      />
                    )}
                  </div>
                </div>
                <div>
                  {isHovering && (
                    <GalleryFileMenu
                      clip={file}
                      getItemMenuOptions={getItemMenuOptions}
                      getSelectionMenuOptions={getSelectionMenuOptions}
                      setIsMenuOpen={setIsMenuOpen}
                      trigger={<CardAction data-testid="FILE_CARD_SUB_MENU_BUTTON" icon={<EllipsisIcon size="sm" />} />}
                    />
                  )}
                </div>
              </div>

              <div className="flex flex-col items-start gap-0.5 p-2">
                <GalleryFileTitle file={file} onClick={onTitleClick} />
                {!!meta && <span className="ads:text-metadata text-12 text-gray-10">{meta}</span>}
              </div>
            </div>
          </div>
          {shouldShowCustomFields && (
            <div className="pointer-events-none z-10 flex flex-col items-stretch gap-2 px-3 pt-1 pb-2">
              <CustomFieldsDisplay customFields={customFields} onClick={onCustomFieldsClick} />
            </div>
          )}
        </>
      )}
    </GallerySelectableCard>
  );
};

_GalleryFile.displayName = '_GalleryFile';

// biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
export const GalleryFile = memo(_GalleryFile) as typeof _GalleryFile;

GalleryFile.displayName = 'GalleryFile';
